import { useEffect, useRef, useState } from "react";
import { Pause, Square, Play, Loader2, AlertCircle, Check } from "lucide-react";
import {
  HireproofRecorder,
  getMicStream,
  getTabAudioStream,
  SOFT_LIMIT_SECONDS,
  HARD_LIMIT_SECONDS,
} from "../../lib/recorder";
import api, { setRecordingActive, isAuthFailure, refreshAccessToken } from "../../lib/api";
import { track } from "../../lib/analytics";
import {
  initRecordingMeta,
  markStopped,
  markFinalized,
  clearRecording,
  listRecoverable,
  ensureUploaded,
  MAX_RETAINED_RECORDINGS,
} from "../../lib/recordingBackup";
import { useAuth } from "../../lib/auth-context";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import CandidatePicker from "../CandidatePicker";
import RecordingErrorBoundary from "./RecordingErrorBoundary";

const STROKE = 1.5;
// Sentinel for "use the workspace's General role" — resolved (and created
// once per workspace) at interview-create time via the EXISTING /roles API.
const DEFAULT_ROLE_SENTINEL = "__default_general__";
const DEFAULT_CONSENT = `Before we start, I'd like to record this interview so we have an accurate record of the conversation. The recording will be used only for internal hiring decisions and stored securely. Do you consent to being recorded?`;

// ---- Stages -----
// setup — E1: ONE screen = candidate type-ahead + location radio + the
//         quiet "Round N · General · Screening [Change]" summary line
// consent — workspace consent script + mandatory checkbox (UNCHANGED)
// recording — start/pause/stop, timer, uploaded chunk count
// processing — show job poll status
// done — success/failure

const DEFAULT_ROLE_TITLE = "General";
const INTERVIEW_TYPES = ["screening", "technical", "behavioral", "panel", "final"];

export default function RecordingFlowController({ open, onOpenChange, prefill }) {
  const { workspace } = useAuth();
  const [stage, setStage] = useState("setup");
  const [error, setError] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);
  const [title, setTitle] = useState(prefill?.title || "");
  // E1 — In person is the default: an HR generalist recording across a desk
  // must never be dropped into a screen-share prompt by accident.
  const [locationType, setLocationType] = useState("in_person"); // in_person / video / phone
  const [interviewType, setInterviewType] = useState("screening");
  const [roleId, setRoleId] = useState(DEFAULT_ROLE_SENTINEL);
  const [roles, setRoles] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Phase 4d — candidate + round selection
  const [candidateId, setCandidateId] = useState(prefill?.candidate_id || null);
  const [candidateName, setCandidateName] = useState(prefill?.candidate_name || "");
  const [candidateRoundCount, setCandidateRoundCount] = useState(prefill?.round_count ?? 0);
  const [roundNumber, setRoundNumber] = useState(null);   // null = let server auto-compute
  const [interview, setInterview] = useState(null);
  const [recording, setRecording] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [chunks, setChunks] = useState({ uploaded: 0, emitted: 0 });
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState(null);
  // Addition 1 — on-audio consent capture. `consentSpokenAt` is the elapsed
  // recording seconds at the moment the recruiter confirms. null == not
  // yet confirmed → the consent panel stays sticky over the recording UI.
  const [consentSpokenAt, setConsentSpokenAt] = useState(null);
  const [consentSaving, setConsentSaving] = useState(false);
  // F1 — set when finalize is blocked on missing/failed chunk uploads.
  const [uploadGap, setUploadGap] = useState(null);
  // Auth-resilience: "preflight" = token too stale to start safely;
  // "recovery" = finalize failed mid-save, audio is on-device only.
  const [authFailKind, setAuthFailKind] = useState(null);
  const recorderRef = useRef(null);
  const timerRef = useRef(null);
  const pollRef = useRef(null);
  // Phase 5 — proactive access-token refresh while recording (every ~20 min,
  // under the 30-min TTL) so the in-memory token never expires mid-interview.
  const refreshTimerRef = useRef(null);
  // Guards against a double finalize when BOTH the in-app Stop button and the
  // browser's native "Stop sharing" control fire. Reset whenever the dialog
  // reopens.
  const stoppingRef = useRef(false);
  // Holds the latest handleStop so the screen-share 'ended' listener (captured
  // once at recording start) always calls the current version, never a stale one.
  const handleStopRef = useRef(null);
  // F1 — duration/mime/chunk_count captured at stop-time so "Retry saving"
  // can re-run finalize with the same metadata.
  const finalizeMetaRef = useRef(null);

  const consentText = workspace?.consent_text_override || DEFAULT_CONSENT;

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setStage("setup");
      setError("");
      setConsentChecked(false);
      setTitle(prefill?.title || "");
      setLocationType("in_person");
      setInterviewType("screening");
      setRoleId(DEFAULT_ROLE_SENTINEL);
      setDetailsOpen(false);
      setCandidateId(prefill?.candidate_id || null);
      setCandidateName(prefill?.candidate_name || "");
      setCandidateRoundCount(prefill?.round_count ?? 0);
      setRoundNumber((prefill?.round_count ?? 0) + 1);
      setInterview(null);
      setRecording(null);
      setElapsed(0);
      setChunks({ uploaded: 0, emitted: 0 });
      setSubmitting(false);
      setJob(null);
      setConsentSpokenAt(null);
      setConsentSaving(false);
      setUploadGap(null);
      finalizeMetaRef.current = null;
      setAuthFailKind(null);
      setRecordingActive(false);
      stoppingRef.current = false;
    }
  }, [open, prefill]);

  // E1 — role list for the collapsed "Change" panel. If the workspace already
  // has a role titled "General" we preselect it so no duplicate is created.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api
      .get("/roles")
      .then(({ data }) => {
        if (cancelled) return;
        setRoles(data || []);
        const general = (data || []).find(
          (r) => (r.title || "").trim().toLowerCase() === DEFAULT_ROLE_TITLE.toLowerCase()
        );
        if (general) setRoleId(general.role_id);
      })
      .catch(() => { /* roles are optional — the sentinel still works */ });
    return () => { cancelled = true; };
  }, [open]);

  // Resolve the sentinel to a real role_id. Re-reads /roles first so two
  // recruiters starting an interview at the same time don't create two
  // "General" rows; only creates when none exists.
  const resolveRoleId = async () => {
    if (roleId !== DEFAULT_ROLE_SENTINEL) return roleId || null;
    const { data: latest } = await api.get("/roles");
    const existing = (latest || []).find(
      (r) => (r.title || "").trim().toLowerCase() === DEFAULT_ROLE_TITLE.toLowerCase()
    );
    if (existing) {
      setRoles(latest || []);
      setRoleId(existing.role_id);
      return existing.role_id;
    }
    const { data: created } = await api.post("/roles", { title: DEFAULT_ROLE_TITLE });
    setRoles([...(latest || []), created]);
    setRoleId(created.role_id);
    return created.role_id;
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      setRecordingActive(false);
      const r = recorderRef.current;
      if (r && r.recorder && r.recorder.state !== "inactive") {
        try { r.recorder.stop(); } catch { /* ignore */ }
        r.stream?.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  // Browser "Stop sharing" control ended the screen share mid-recording.
  // Finalize gracefully so whatever was captured is preserved (chunks are
  // already uploaded + mirrored on-device), rather than recording dead air.
  // Guarded so it never double-finalizes alongside the in-app Stop button.
  const handleShareEnded = () => {
    if (stoppingRef.current || !recorderRef.current) return;
    toast("Screen sharing stopped — finalizing your recording…");
    handleStopRef.current?.();
  };

  // Called by RecordingErrorBoundary when a stage render throws. Release the
  // recording lock + timers and stop live tracks so nothing keeps running in
  // the background. On-device chunks are intentionally LEFT intact for crash
  // recovery (the sticky recovery banner can re-finalize them).
  const handleFlowError = () => {
    setRecordingActive(false);
    stoppingRef.current = false;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (refreshTimerRef.current) { clearInterval(refreshTimerRef.current); refreshTimerRef.current = null; }
    const r = recorderRef.current;
    if (r && r.recorder && r.recorder.state !== "inactive") {
      try { r.recorder.stop(); } catch { /* ignore */ }
      r.stream?.getTracks().forEach((t) => t.stop());
    }
  };

  // "Try again" from the error-boundary fallback → back to the pre-record screen.
  const handleFlowRetry = () => {
    setError("");
    setRecording(null);
    setJob(null);
    setStage("setup");
  };

  // ---- Step 2: consent confirmed → create interview + record consent ----
  const startInterview = async () => {
    // Capacity warning (never silent auto-discard): if the local recovery
    // store is already at the cap, warn the human to recover/discard first.
    try {
      const pending = await listRecoverable();
      if (pending.length >= MAX_RETAINED_RECORDINGS) {
        toast.warning(
          `${pending.length} unsaved recordings are still on this device. Recover or discard them from the banner above — local backup space is limited.`
        );
      }
    } catch { /* best-effort */ }
    // Prevention over recovery: ensure a valid session BEFORE recording. A
    // successful refresh mints a fresh 30-min access token AND confirms the
    // refresh cookie is still valid (so mid-recording renewals will work). If
    // it fails, prompt re-sign-in now rather than losing audio at finalize.
    const sessionOk = await refreshAccessToken();
    if (!sessionOk) {
      setAuthFailKind("preflight");
      setStage("auth_failed");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      // Reuse the already-created interview when the recording start failed
      // (e.g. the browser share dialog was cancelled) — retrying must never
      // create a second interview or a second consent record.
      let withConsent = interview;
      if (!withConsent) {
        const resolvedRoleId = await resolveRoleId();
        const { data: interviewDoc } = await api.post("/interviews", {
          title: title || (candidateName ? `${candidateName} — Round ${roundNumber || 1}` : "Untitled interview"),
          type: interviewType,
          location_type: locationType,
          candidate_id: candidateId,
          role_id: resolvedRoleId,
          round_number: roundNumber,
        });
        const { data: consented } = await api.post(
          `/interviews/${interviewDoc.interview_id}/consent`,
          {
            consent_method: "checkbox_only",
            consent_text: consentText,
          }
        );
        withConsent = consented;
        setInterview(consented);
      }
      // E1 — no preflight screen: In person goes straight to the mic, Online
      // goes straight to the single browser share dialog.
      await beginRecording(withConsent, locationType === "video" ? "tab" : "mic");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to start interview");
    } finally {
      setSubmitting(false);
    }
  };

  const beginRecording = async (interviewDoc, source) => {
    setSubmitting(true);
    setError("");
    try {
      const stream =
        source === "tab"
          ? await getTabAudioStream({ onShareEnded: handleShareEnded })
          : await getMicStream();
      const { data: recordingDoc } = await api.post(
        `/interviews/${interviewDoc.interview_id}/recordings`,
        {}
      );
      setRecording(recordingDoc);
      const rec = new HireproofRecorder({
        recordingId: recordingDoc.recording_id,
        stream,
        onChunkProgress: ({ uploaded, emitted }) => setChunks({ uploaded, emitted }),
        onError: (err) => {
          console.error("recorder err", err);
          toast.error(err.message || "Recording error");
        },
      });
      // Phase auth-(b): register this recording in the on-device recovery store
      // BEFORE the first chunk lands, so even an immediate page-death leaves a
      // recoverable entry tied to the EXISTING interview_id (consent intact).
      await initRecordingMeta({
        recordingId: recordingDoc.recording_id,
        interviewId: interviewDoc.interview_id,
        candidateName: candidateName || interviewDoc.title || "Candidate",
        roundNumber: roundNumber,
        mimeType: rec.mimeType,
        startedAt: new Date().toISOString(),
      });
      await rec.start();
      recorderRef.current = rec;
      // Critical window OPEN: from here until finalize is server-confirmed, a
      // 401 must never hard-logout / navigate away and lose this interview.
      setRecordingActive(true);
      setStage("recording");
      // Proactive token refresh — keep the in-memory access token fresh so it
      // never expires mid-interview. The single-flight refresh in api.js
      // collapses this with any reactive refresh-on-401, so a rotated refresh
      // token is never replayed (reuse-detection can't revoke the family here).
      refreshTimerRef.current = setInterval(() => {
        refreshAccessToken();
      }, 20 * 60 * 1000);
      timerRef.current = setInterval(() => {
        const seconds = rec.elapsedSeconds();
        setElapsed(seconds);
        // Phase 2a — auto-stop at hard cap (120 min). The setInterval keeps
        // firing every 250ms while the recorder is alive; once we cross the
        // threshold we clear it and trigger the same stopAndFinalize path
        // the user-initiated Stop button uses, so finalization + transcript
        // flow are identical for human-stopped and auto-stopped recordings.
        if (seconds >= HARD_LIMIT_SECONDS) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          toast("Maximum interview length reached (120 min). Finalizing recording…");
          // Call the same handler; it's a closure over component state so
          // it'll bump through processing → done normally.
          handleStop();
        }
      }, 250);
    } catch (err) {
      // Scoped to the online (getDisplayMedia) path: map the common failure
      // modes to clear, recoverable messages and return to the pre-record
      // (preflight) screen. The mic path keeps its original behavior.
      let msg;
      if (source === "tab") {
        if (err?.code === "NO_TAB_AUDIO") {
          msg = 'No tab audio detected. Press Continue to try again and tick "Share tab audio" in the browser dialog — without it we only capture your mic.';
        } else if (
          err?.name === "NotAllowedError" ||
          /permission|denied|cancel|dismiss|abort/i.test(err?.message || "")
        ) {
          msg = "Screen sharing was cancelled or blocked. Press Continue to try again, or go back and switch to In person.";
        } else {
          msg = err?.message || "Couldn’t start screen recording. Please try again.";
        }
      } else {
        msg = err?.message || "Failed to start recording";
      }
      setError(msg);
      setRecordingActive(false);
      setStage("consent");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePauseToggle = () => {
    const r = recorderRef.current;
    if (!r) return;
    if (r.isPaused()) {
      r.resume();
    } else {
      r.pause();
    }
    // Force re-render
    setChunks((c) => ({ ...c }));
  };

  // Addition 1 — persist the on-audio consent anchor to the server. The
  // pre-recording checkbox gate is UNCHANGED (still enforced at every
  // downstream endpoint); this call only stamps the moment the recruiter
  // confirms the audible exchange, so PDF/transcript views can jump to it.
  // Crash-safe: consent_spoken_at_seconds lives on the interview server-side
  // so if the tab dies after confirmation, the resumed session sees it as
  // already captured on next GET /interviews/:id.
  const handleConsentSpokenConfirm = async () => {
    if (!interview?.interview_id || consentSpokenAt != null) return;
    const seconds = Math.max(0, Math.round(elapsed));
    setConsentSaving(true);
    try {
      const { data } = await api.post(
        `/interviews/${interview.interview_id}/consent-spoken`,
        { elapsed_seconds: seconds }
      );
      setInterview(data);
      setConsentSpokenAt(seconds);
      toast.success("Consent captured — you can proceed with the interview.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't capture consent — please try again.");
    } finally {
      setConsentSaving(false);
    }
  };

  // F1 — drain any failed/straggler chunks from the on-device mirror via the
  // EXISTING recovery uploader (recordingBackup.ensureUploaded — no parallel
  // drain path), then finalize with the client-reported chunk_count so the
  // server can verify completeness. Returns false (and shows the
  // upload_incomplete stage) when segments are still missing — the IndexedDB
  // copy is left INTACT so retry/recovery can finish the save. Only a
  // server-CONFIRMED, complete finalize clears the local copy.
  const drainAndFinalize = async () => {
    const meta = finalizeMetaRef.current;
    const drain = await ensureUploaded(recording.recording_id);
    if (drain.failed > 0) {
      setUploadGap({
        message: `${drain.failed} of ${drain.total} audio segment${drain.total === 1 ? "" : "s"} couldn't be uploaded.`,
      });
      setStage("upload_incomplete");
      window.dispatchEvent(new CustomEvent("hireproof:recovery-changed"));
      return false;
    }
    try {
      // Tell server to stitch chunks & store in GridFS. This is THE critical
      // save — give it extra grip (bounded retries) since its failure = lost
      // recording. A 401 here is refresh+retried up to 3x before giving up.
      await api.post(`/recordings/${recording.recording_id}/finalize`, {
        duration_seconds: meta.duration_seconds,
        mime_type: meta.mime_type,
        chunk_count: meta.chunk_count,
      }, { _authMaxRetries: 3 });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "chunks_missing") {
        // Server-side integrity gate: it refuses to stitch a gapped file.
        // Do NOT clear the on-device copy — it's the only repair source.
        setUploadGap({
          message: detail.message || "Some audio segments are missing on the server.",
        });
        setStage("upload_incomplete");
        window.dispatchEvent(new CustomEvent("hireproof:recovery-changed"));
        return false;
      }
      throw err;
    }
    // finalize SERVER-CONFIRMED and COMPLETE → the recording is safely
    // persisted. Only now is it honest to consider the critical window
    // closed AND to clear the on-device recovery copy.
    setRecordingActive(false);
    track("interview_recorded", {
      duration_seconds: meta.duration_seconds,
      chunk_count: meta.chunk_count,
    });
    await markFinalized(recording.recording_id);
    await clearRecording(recording.recording_id);
    window.dispatchEvent(new CustomEvent("hireproof:recovery-changed"));
    return true;
  };

  // CR-2 — kick off transcription for the (already finalized) recording and
  // poll the job. Failures land on the transcribe_failed stage: the recording
  // IS saved at this point, so the honest state is "saved, transcription not
  // started" with a retry — never the recovery/"audio only on device" messaging.
  const startTranscription = async () => {
    setSubmitting(true);
    try {
      const { data: jobDoc } = await api.post("/transcribe", {
        recording_id: recording.recording_id,
      });
      setJob(jobDoc);
      setStage("processing");
      // Poll
      pollRef.current = setInterval(async () => {
        try {
          const { data: latest } = await api.get(`/transcribe/${jobDoc.job_id}`);
          setJob(latest);
          if (latest.status === "completed" || latest.status === "failed") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            if (latest.status === "completed") track("transcription_completed", {});
            setStage("done");
          }
        } catch (err) {
          // ignore transient poll errors; keep trying
        }
      }, 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError((typeof detail === "string" && detail) || err?.message || "Couldn't start transcription");
      setStage("transcribe_failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Retry from the processing dialog once the wait crosses the watchdog
  // window. The retry endpoint only accepts failed/completed jobs, so while
  // the job is still technically "running" we say so plainly instead of
  // throwing a raw 400 at the user.
  const retryStuckTranscription = async () => {
    if (!job?.job_id) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/transcribe/${job.job_id}/retry`);
      setJob(data);
      toast.success("Retrying transcription");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 400) {
        toast.info("Still wrapping up — it will be marked failed within a minute, then Retry will work.");
      } else {
        toast.error((typeof detail === "string" && detail) || "Couldn't retry transcription");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Shared by Stop and the "Retry saving" action on the upload_incomplete
  // stage: drain → finalize → kick transcription → poll.
  const finalizePipeline = async () => {
    setSubmitting(true);
    setStage("processing");
    setError("");
    try {
      const ok = await drainAndFinalize();
      if (!ok) return; // upload_incomplete stage shown; on-device copy intact
      await startTranscription();
    } catch (err) {
      if (isAuthFailure(err)) {
        // Recovery exhausted during the critical save. Be honest: the audio is
        // captured on THIS DEVICE (IndexedDB) but NOT yet on the server — do
        // not claim it's saved. Chunks are left intact for recovery, and the
        // sticky banner is notified so it surfaces this recording right away.
        setAuthFailKind("recovery");
        setStage("auth_failed");
        window.dispatchEvent(new CustomEvent("hireproof:recovery-changed"));
      } else {
        const detail = err?.response?.data?.detail;
        setError((typeof detail === "string" && detail) || err?.message || "Processing failed");
        setStage("done");
      }
    } finally {
      setSubmitting(false);
      setRecordingActive(false);
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    }
  };

  const handleStop = async () => {
    const r = recorderRef.current;
    if (!r) return;
    // Guard against a double finalize (in-app Stop + browser "Stop sharing").
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    setSubmitting(true);
    setStage("processing");
    setError("");
    if (timerRef.current) clearInterval(timerRef.current);
    try {
      const meta = await r.stopAndFinalize();
      // Record the final duration locally before finalize so a recovery later
      // finalizes with the correct length.
      await markStopped(recording.recording_id, meta.duration_seconds);
      finalizeMetaRef.current = {
        duration_seconds: meta.duration_seconds,
        mime_type: meta.mime_type,
        chunk_count: meta.chunk_count,
      };
    } catch (err) {
      setSubmitting(false);
      setRecordingActive(false);
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      setError(err?.message || "Processing failed");
      setStage("done");
      return;
    }
    await finalizePipeline();
  };

  // Keep the ref pointed at the latest handleStop so the share-ended listener
  // (captured once at recording start) always calls the current version.
  handleStopRef.current = handleStop;

  const fmtTime = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const sec = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  const handleClose = () => {
    if (stage === "recording") {
      if (!confirm("Recording in progress. Discard and close?")) return;
      const r = recorderRef.current;
      if (r) {
        try { r.recorder.stop(); } catch { /* ignore */ }
        r.stream?.getTracks().forEach((t) => t.stop());
      }
      setRecordingActive(false); // discarded — critical window closed
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(true); }}>
      <DialogContent
        className="max-w-xl"
        data-testid="recording-flow"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => stage !== "recording" || e.preventDefault()}
      >
        <RecordingErrorBoundary
          onError={handleFlowError}
          onReset={handleFlowRetry}
          onClose={() => onOpenChange(false)}
        >
        {stage === "setup" && (
          <SetupStage
            candidateName={candidateName}
            candidateLocked={Boolean(prefill?.candidate_id)}
            candidateRoundCount={candidateRoundCount}
            roundNumber={roundNumber}
            setRoundNumber={setRoundNumber}
            locationType={locationType}
            setLocationType={setLocationType}
            interviewType={interviewType}
            setInterviewType={setInterviewType}
            roleId={roleId}
            setRoleId={setRoleId}
            roles={roles}
            title={title}
            setTitle={setTitle}
            detailsOpen={detailsOpen}
            setDetailsOpen={setDetailsOpen}
            onPick={({ candidate_id, candidate_name, round_count }) => {
              setCandidateId(candidate_id);
              setCandidateName(candidate_name);
              setCandidateRoundCount(round_count);
              setRoundNumber(round_count + 1);
            }}
            onClear={() => {
              setCandidateId(null);
              setCandidateName("");
              setCandidateRoundCount(0);
              setRoundNumber(null);
            }}
            onContinue={() => { setError(""); setStage("consent"); }}
            onCancel={() => onOpenChange(false)}
            ready={Boolean(candidateId)}
          />
        )}
        {stage === "consent" && (
          <ConsentStage
            consentText={consentText}
            consentChecked={consentChecked}
            setConsentChecked={setConsentChecked}
            submitting={submitting}
            error={error}
            candidateName={candidateName}
            roundNumber={roundNumber}
            locationType={locationType}
            onBack={() => setStage("setup")}
            onContinue={startInterview}
            onCancel={() => onOpenChange(false)}
          />
        )}
        {stage === "recording" && (
          <RecordingStage
            elapsed={elapsed}
            isPaused={recorderRef.current?.isPaused?.()}
            chunks={chunks}
            onPauseToggle={handlePauseToggle}
            onStop={handleStop}
            title={interview?.title}
            fmtTime={fmtTime}
            consentText={consentText}
            candidateName={candidateName}
            consentSpokenAt={consentSpokenAt ?? interview?.consent_spoken_at_seconds ?? null}
            consentSaving={consentSaving}
            onConfirmConsent={handleConsentSpokenConfirm}
          />
        )}
        {stage === "processing" && (
          <ProcessingStage
            job={job}
            error={error}
            fmtTime={fmtTime}
            elapsedSeconds={elapsed}
            onRetry={retryStuckTranscription}
            retrying={submitting}
          />
        )}
        {stage === "upload_incomplete" && (
          <UploadIncompleteStage
            info={uploadGap}
            submitting={submitting}
            onRetry={finalizePipeline}
            onClose={() => onOpenChange(false)}
          />
        )}
        {stage === "transcribe_failed" && (
          <TranscribeFailedStage
            error={error}
            submitting={submitting}
            interview={interview}
            onRetry={startTranscription}
            onClose={() => onOpenChange(false)}
          />
        )}
        {stage === "done" && (
          <DoneStage job={job} error={error} interview={interview} onClose={() => onOpenChange(false)} />
        )}
        {stage === "auth_failed" && (
          <AuthFailedStage
            kind={authFailKind}
            onReauth={() => {
              // Hard navigate to sign-in; reason drives the banner. After
              // re-auth, recovery (Phase b) picks up any on-device chunks.
              const reason = authFailKind === "recovery" ? "session_expired&recover=1" : "session_expired";
              window.location.assign(`/login?reason=${reason}`);
            }}
            onClose={() => onOpenChange(false)}
          />
        )}
        </RecordingErrorBoundary>
      </DialogContent>
    </Dialog>
  );
}

function AuthFailedStage({ kind, onReauth, onClose }) {
  const isRecovery = kind === "recovery";
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl font-medium" data-testid="auth-failed-title">
          {isRecovery ? "Sign-in needed to save this recording" : "Refresh your session first"}
        </DialogTitle>
        <DialogDescription className="sr-only">Session re-authentication required</DialogDescription>
      </DialogHeader>
      <div className="space-y-4" data-testid="auth-failed-stage">
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={STROKE} />
          {isRecovery ? (
            <span>
              Your session expired while saving. <strong>Your audio is captured on this device</strong> — it has
              <strong> not</strong> been lost, but it isn’t on the server yet. Sign in again and we’ll finish saving it.
            </span>
          ) : (
            <span>
              Your session is close to expiring. Sign in again before recording so your interview saves without
              interruption.
            </span>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md border border-ink-200 px-4 py-2 text-[13px] font-medium text-ink-600 hover:bg-ink-50"
            data-testid="auth-failed-close"
          >
            Close
          </button>
          <button
            onClick={onReauth}
            className="rounded-md bg-emerald-800 px-4 py-2 text-[13px] font-medium text-white hover:bg-emerald-900"
            data-testid="auth-failed-reauth"
          >
            {isRecovery ? "Sign in to recover" : "Re-sign in"}
          </button>
        </div>
      </div>
    </>
  );
}

// F1 — shown when Stop couldn't complete the save because audio segments are
// missing (client-side drain failed, or the server's contiguity gate refused
// to stitch a gapped file). The on-device IndexedDB copy is intact; the user
// can retry here or later from the sticky recovery banner. NEVER a silent success.
function UploadIncompleteStage({ info, submitting, onRetry, onClose }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl font-medium" data-testid="upload-incomplete-title">
          Some audio hasn&rsquo;t reached the server yet
        </DialogTitle>
        <DialogDescription className="sr-only">Recording upload incomplete</DialogDescription>
      </DialogHeader>
      <div className="space-y-4" data-testid="upload-incomplete-stage">
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={STROKE} />
          <span data-testid="upload-incomplete-detail">
            {info?.message || "Some audio segments failed to upload."}{" "}
            <strong>Your audio is safe on this device</strong> — nothing has been saved
            incompletely. Check your connection and retry to finish saving. If you close
            this dialog, the recording stays available in the recovery banner above.
          </span>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="hp-btn-ghost" data-testid="upload-incomplete-close">
            Close
          </button>
          <button
            onClick={onRetry}
            disabled={submitting}
            className="hp-btn-primary"
            data-testid="upload-incomplete-retry"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} />}
            Retry saving
          </button>
        </div>
      </div>
    </>
  );
}

// CR-2 — the recording finalized successfully but the transcription job
// couldn't be started (transient network / server error). The audio is safe
// on the server; offer a direct retry plus a path to the interview page,
// which has its own persistent "Start transcription" affordance.
function TranscribeFailedStage({ error, submitting, interview, onRetry, onClose }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl font-medium" data-testid="transcribe-failed-title">
          Recording saved — transcription hasn&rsquo;t started
        </DialogTitle>
        <DialogDescription className="sr-only">Transcription failed to start</DialogDescription>
      </DialogHeader>
      <div className="space-y-4" data-testid="transcribe-failed-stage">
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={STROKE} />
          <span data-testid="transcribe-failed-detail">
            Your audio is <strong>safely saved on the server</strong>, but transcription
            couldn&rsquo;t start{error ? `: ${error}` : "."} You can retry now, or start it
            later from the interview page.
          </span>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="hp-btn-ghost" data-testid="transcribe-failed-close">
            Close
          </button>
          {interview?.interview_id && (
            <a
              href={`/interviews/${interview.interview_id}`}
              className="hp-btn-outline"
              data-testid="transcribe-failed-open-interview"
            >
              Open interview
            </a>
          )}
          <button
            onClick={onRetry}
            disabled={submitting}
            className="hp-btn-primary"
            data-testid="transcribe-failed-retry"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} />}
            Retry transcription
          </button>
        </div>
      </div>
    </>
  );
}

function ConsentStage({ consentText, consentChecked, setConsentChecked, submitting, error, candidateName, roundNumber, locationType, onBack, onContinue, onCancel }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl font-medium" data-testid="consent-title">
          Capture consent
        </DialogTitle>
        <DialogDescription className="text-[13px] text-ink-500">
          Two-party consent is the default. Read the consent script aloud and check the box only after the candidate has verbally agreed.
        </DialogDescription>
      </DialogHeader>
      {candidateName && (
        <div className="rounded-md border border-emerald-800/20 bg-emerald-50/40 px-3 py-2 text-[12px] text-emerald-900" data-testid="consent-candidate-summary">
          Recording for <strong>{candidateName}</strong>
          {roundNumber ? ` · Round ${roundNumber}` : ""}
          {locationType === "in_person" ? " · In person" : " · Online call"}
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="ml-2 underline underline-offset-2 hover:text-emerald-700"
              data-testid="consent-change-candidate"
            >
              Change
            </button>
          )}
        </div>
      )}
      <div className="space-y-5 py-2">
        <div>
          <div className="text-[12px] font-medium uppercase tracking-wider text-ink-500">
            Consent script
          </div>
          <div
            className="mt-1.5 rounded-md border border-ink-200 bg-ink-50 px-4 py-3 font-display text-[14px] leading-relaxed text-ink-900"
            data-testid="consent-script"
          >
            {consentText}
          </div>
          <p className="mt-2 text-[12px] text-ink-400">
            Admins can edit this default in Settings. Assign hiring managers from the interview page after this one is created.
          </p>
        </div>
        {locationType === "video" && (
          <div
            className="rounded-md border border-ink-200 bg-white px-3 py-2.5 text-[12px] text-ink-600"
            data-testid="online-share-instructions"
          >
            <div className="font-medium text-ink-900">After Continue, your browser asks what to share</div>
            <ol className="mt-1.5 space-y-1 list-decimal list-inside">
              <li>Pick the <span className="font-medium text-ink-900">tab</span> running your meeting.</li>
              <li><span className="font-medium text-emerald-800">Tick &ldquo;Share tab audio&rdquo;</span> — without this we only capture your mic.</li>
              <li>Click <span className="font-medium text-ink-900">Share</span>.</li>
            </ol>
          </div>
        )}
        <div
          className="rounded-md border border-ink-200 bg-white px-3 py-2 text-[12px] text-ink-500"
          data-testid="consent-duration-hint"
        >
          Max interview length: 120 min. We&rsquo;ll automatically stop the recording at the limit.
        </div>
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={consentChecked}
            onChange={(e) => setConsentChecked(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-ink-300 text-emerald-800 focus:ring-emerald-800/30"
            data-testid="consent-checkbox"
          />
          <span className="text-[13px] text-ink-700">
            I have read the script aloud and obtained the candidate&rsquo;s verbal consent to record this interview.
          </span>
        </label>
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-[13px] text-red-700" data-testid="consent-error">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={STROKE} /> {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onCancel} className="hp-btn-ghost" data-testid="consent-cancel">Cancel</button>
          <button
            onClick={onContinue}
            disabled={!consentChecked || submitting}
            className="hp-btn-primary"
            data-testid="consent-continue"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} />}
            Continue
          </button>
        </div>
      </div>
    </>
  );
}

function RecordingStage({ elapsed, isPaused, chunks, onPauseToggle, onStop, title, fmtTime, consentText, candidateName, consentSpokenAt, consentSaving, onConfirmConsent }) {
  // Simple animated bar pulse during recording (no real waveform — keeps the
  // bundle small and the visual cue clear: red dot + bars + timer is enough).
  const approachingSoftLimit = elapsed >= SOFT_LIMIT_SECONDS;
  const consentCaptured = consentSpokenAt != null;
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl font-medium flex items-center gap-3" data-testid="recording-title">
          <span className="relative inline-flex">
            <span className={`inline-block h-2.5 w-2.5 rounded-full ${isPaused ? "bg-amber-500" : "bg-red-600 animate-pulse"}`} />
          </span>
          {isPaused ? "Paused" : "Recording"}
        </DialogTitle>
        <DialogDescription className="text-[13px] text-ink-500">
          {title || "Interview in progress"}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-6 py-2">
        {/* Addition 1 — on-audio consent capture. Not dismissible until
            confirmed; that's the nudge. Collapses to a persistent one-liner
            for the rest of the session once the recruiter confirms. */}
        {!consentCaptured ? (
          <div
            className="rounded-md border-2 border-emerald-800 bg-emerald-50/60 p-4"
            data-testid="on-audio-consent-panel"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-900">
                Read this to the candidate — recording live
              </div>
              <div className="font-mono text-[13px] text-emerald-900 tabular-nums" data-testid="on-audio-consent-timer">
                {fmtTime(elapsed)}
              </div>
            </div>
            <div className="font-display text-[15px] leading-relaxed text-ink-900" data-testid="on-audio-consent-script">
              {consentText}
            </div>
            <div className="mt-3 text-[12px] text-ink-600">
              Read aloud so the exchange is captured on the audio. Only tap below after {candidateName || "the candidate"} has verbally agreed.
            </div>
            <button
              onClick={onConfirmConsent}
              disabled={consentSaving}
              className="hp-btn-primary mt-3 w-full"
              data-testid="on-audio-consent-confirm"
            >
              {consentSaving && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} />}
              Candidate has given consent, continue
            </button>
          </div>
        ) : (
          <div
            className="flex items-center justify-between rounded border border-emerald-200 bg-emerald-50/60 px-3 py-1.5"
            data-testid="on-audio-consent-captured-chip"
          >
            <span className="text-[12px] text-emerald-900">
              Consent captured at <span className="font-mono tabular-nums">{fmtTime(consentSpokenAt)}</span>
            </span>
          </div>
        )}
        {approachingSoftLimit && (
          <div
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800"
            data-testid="recording-soft-limit-banner"
          >
            <AlertCircle className="mt-0.5 h-4 w-4" strokeWidth={STROKE} />
            <span>
              Approaching standard max — consider wrapping up. Hard cap at 120 min.
            </span>
          </div>
        )}
        <div className="rounded-lg border border-ink-200 bg-ink-50 p-6 text-center">
          <div className="font-mono font-display text-5xl tabular-nums tracking-tight text-ink-900" data-testid="recording-elapsed">
            {fmtTime(elapsed)}
          </div>
          <div className="mt-2 text-[12px] uppercase tracking-wider text-ink-500">
            Elapsed
          </div>
        </div>
        <div className="flex items-center justify-center gap-2">
          {[...Array(20)].map((_, i) => (
            <span
              key={i}
              className={`block w-1 rounded-full bg-emerald-800 ${isPaused ? "opacity-30" : ""}`}
              style={{
                height: `${10 + Math.random() * 24}px`,
                animation: isPaused ? "none" : `wf-${i % 4} 0.6s ease-in-out infinite alternate`,
              }}
            />
          ))}
        </div>
        <style>{`
          @keyframes wf-0 { from { height: 8px } to { height: 24px } }
          @keyframes wf-1 { from { height: 12px } to { height: 32px } }
          @keyframes wf-2 { from { height: 16px } to { height: 22px } }
          @keyframes wf-3 { from { height: 6px } to { height: 28px } }
        `}</style>
        <div className="rounded-md border border-ink-200 bg-white p-3 text-[12px] text-ink-500" data-testid="chunk-status">
          Uploaded {chunks.uploaded}/{chunks.emitted} chunks · audio is streaming to the server in real time.
        </div>
        <div className="flex items-center justify-center gap-3">
          <button onClick={onPauseToggle} className="hp-btn-outline" data-testid="recording-pause">
            {isPaused ? <Play className="h-4 w-4" strokeWidth={STROKE} /> : <Pause className="h-4 w-4" strokeWidth={STROKE} />}
            {isPaused ? "Resume" : "Pause"}
          </button>
          <button onClick={onStop} className="hp-btn-primary" data-testid="recording-stop">
            <Square className="h-4 w-4 fill-white" strokeWidth={STROKE} />
            Stop &amp; transcribe
          </button>
        </div>
      </div>
    </>
  );
}

function ProcessingStage({ job, error, fmtTime, elapsedSeconds, onRetry, retrying }) {
  // Real pipeline stages, in order, mapped from the backend's own stage names.
  // No synthetic percentage — the bar used to read "25%" for the entire ASR
  // wait, which made a stalled job look like a progressing one.
  const STAGES = [
    { key: "uploaded", label: "Uploaded", matches: ["queued", "recovering"] },
    { key: "sent", label: "Sent for transcription", matches: ["loading_audio"] },
    { key: "transcribing", label: "Transcribing", matches: ["transcribing"] },
    { key: "summary", label: "Writing summary", matches: ["building_transcript", "summarizing_and_classifying", "summarizing"] },
    { key: "done", label: "Done", matches: ["done"] },
  ];
  const stage = job?.stage || "queued";
  const failed = job?.status === "failed";
  const completed = job?.status === "completed";
  let activeIdx = STAGES.findIndex((s) => s.matches.includes(stage));
  if (completed) activeIdx = STAGES.length - 1;
  if (activeIdx < 0) activeIdx = 0;

  const [waited, setWaited] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setWaited((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);
  // Matches JOB_HEARTBEAT_STALE_MINUTES on the backend (10 min).
  const WATCHDOG_SECONDS = 600;
  const overdue = waited > WATCHDOG_SECONDS && !completed && !failed;

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl font-medium" data-testid="processing-title">
          Processing your interview
        </DialogTitle>
        <DialogDescription className="text-[13px] text-ink-500">
          You can close this dialog — the transcript appears on the interview page when it&rsquo;s
          ready. Most of the wait is fixed setup per recording, so even a 30-second clip
          usually takes a minute or two.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-5 py-3">
        <div className="rounded-md border border-ink-200 bg-ink-50 p-4" data-testid="processing-stage-list">
          <ul className="space-y-2">
            {STAGES.map((s, i) => {
              const state = i < activeIdx ? "done" : i === activeIdx ? "active" : "pending";
              return (
                <li
                  key={s.key}
                  className="flex items-center gap-2.5 text-[13px]"
                  data-testid={`processing-stage-${s.key}`}
                  data-state={state}
                >
                  <span
                    className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                      state === "done"
                        ? "border-emerald-800 bg-emerald-800 text-white"
                        : state === "active"
                        ? "border-emerald-800 bg-white"
                        : "border-ink-300 bg-white"
                    }`}
                  >
                    {state === "done" && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
                    {state === "active" && !failed && (
                      <Loader2 className="h-2.5 w-2.5 animate-spin text-emerald-800" strokeWidth={3} />
                    )}
                  </span>
                  <span className={state === "pending" ? "text-ink-400" : "text-ink-900"}>
                    {s.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="flex items-center justify-between text-[12px] text-ink-500">
          <span>
            Recording length: <span className="font-medium text-ink-900">{fmtTime(elapsedSeconds)}</span>
          </span>
          <span data-testid="processing-elapsed">
            Time waiting: <span className="font-medium text-ink-900">{fmtTime(waited)}</span>
          </span>
        </div>
        {overdue && (
          <div
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-900"
            data-testid="processing-overdue"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={STROKE} />
            <div className="space-y-2">
              <div>
                This has been running for over 10 minutes. Your audio is saved. If the
                engine doesn&rsquo;t respond it will be marked failed automatically within a
                minute, and you can retry it here or from the interview page.
              </div>
              <button
                onClick={onRetry}
                disabled={retrying}
                className="hp-btn-outline"
                data-testid="processing-retry"
              >
                {retrying && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} />}
                Retry transcription
              </button>
            </div>
          </div>
        )}
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-[13px] text-red-700" data-testid="processing-error">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={STROKE} /> {error}
          </div>
        )}
      </div>
    </>
  );
}

function DoneStage({ job, error, interview, onClose }) {
  const success = job?.status === "completed";
  const interviewId = job?.result?.interview_id || interview?.interview_id;
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl font-medium" data-testid="done-title">
          {success ? "Interview saved" : "Something went wrong"}
        </DialogTitle>
        <DialogDescription className="text-[13px] text-ink-500">
          {success
            ? "Transcript and summary are ready. You can review, re-label speakers, and add a scorecard."
            : (job?.error || error || "The transcription pipeline failed. You can retry from the Interview detail page.")}
        </DialogDescription>
      </DialogHeader>
      <div className="flex justify-end gap-2 py-3">
        <button onClick={onClose} className="hp-btn-ghost" data-testid="done-close">Close</button>
        {interviewId && (
          <a
            href={`/interviews/${interviewId}`}
            className="hp-btn-primary"
            data-testid="done-open-interview"
          >
            Open interview
          </a>
        )}
      </div>
    </>
  );
}

// E1 — ONE pre-consent screen: who, where, and a quiet defaults line.
// Candidate is MANDATORY: Continue stays disabled until one is picked or
// created inline (name only; email / LinkedIn live on the candidate page).
function SetupStage({
  candidateName, candidateLocked, candidateRoundCount, roundNumber, setRoundNumber,
  locationType, setLocationType, interviewType, setInterviewType,
  roleId, setRoleId, roles, title, setTitle, detailsOpen, setDetailsOpen,
  onPick, onClear, onContinue, onCancel, ready,
}) {
  const roleLabel =
    roleId === DEFAULT_ROLE_SENTINEL
      ? DEFAULT_ROLE_TITLE
      : (roles.find((r) => r.role_id === roleId)?.title || DEFAULT_ROLE_TITLE);
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl font-medium" data-testid="setup-stage-title">
          Start an interview
        </DialogTitle>
        <DialogDescription className="text-[13px] text-ink-500">
          Every interview belongs to a candidate so it shows up on their profile and is searchable across rounds.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-5 py-2">
        {/* a. Who */}
        <div>
          <div className="text-[12px] font-medium uppercase tracking-wider text-ink-500">
            Who are you interviewing?
          </div>
          <div className="mt-2">
            {!candidateName ? (
              <CandidatePicker nameOnly onSelect={onPick} />
            ) : (
              <div
                className="flex items-center justify-between gap-2 rounded-md border border-emerald-800/30 bg-emerald-50/40 px-3 py-2"
                data-testid="setup-candidate-selected"
              >
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-medium text-ink-900">{candidateName}</div>
                  <div className="text-[11px] text-ink-500">
                    {candidateRoundCount === 0
                      ? "First round for this candidate"
                      : `${candidateRoundCount} prior round${candidateRoundCount === 1 ? "" : "s"} recorded`}
                  </div>
                </div>
                {!candidateLocked && (
                  <button
                    type="button"
                    onClick={onClear}
                    className="shrink-0 text-[12px] text-ink-500 underline underline-offset-2 hover:text-ink-900"
                    data-testid="setup-candidate-change"
                  >
                    Change
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* b. Where */}
        <div>
          <div className="text-[12px] font-medium uppercase tracking-wider text-ink-500">
            Where is it happening?
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {[
              { v: "in_person", label: "In person", hint: "Microphone only" },
              { v: "video", label: "Online call", hint: "Shares the meeting tab" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setLocationType(opt.v)}
                className={`rounded-md border px-3 py-2 text-left transition ${
                  locationType === opt.v
                    ? "border-emerald-800 bg-emerald-50 text-emerald-900"
                    : "border-ink-200 bg-white text-ink-700 hover:bg-ink-50"
                }`}
                data-testid={`location-${opt.v}`}
                aria-pressed={locationType === opt.v}
              >
                <div className="text-[13px] font-medium">{opt.label}</div>
                <div className="mt-0.5 text-[11px] text-ink-500">{opt.hint}</div>
              </button>
            ))}
          </div>
        </div>

        {/* c. Quiet defaults line */}
        <div className="rounded-md border border-ink-200 bg-ink-50/60 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[12.5px] text-ink-600" data-testid="setup-defaults-summary">
              Round {roundNumber || 1} · {roleLabel} · <span className="capitalize">{interviewType}</span>
            </div>
            <button
              type="button"
              onClick={() => setDetailsOpen(!detailsOpen)}
              className="text-[12px] font-medium text-emerald-800 underline underline-offset-2 hover:text-emerald-900"
              data-testid="setup-change-details"
            >
              {detailsOpen ? "Done" : "Change"}
            </button>
          </div>
          {detailsOpen && (
            <div className="mt-3 space-y-3 border-t border-ink-200 pt-3" data-testid="setup-details-panel">
              <div className="flex items-center gap-2">
                <label className="w-[72px] text-[11px] uppercase tracking-wider text-ink-500">Round</label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={roundNumber || 1}
                  onChange={(e) => setRoundNumber(Math.max(1, Math.min(20, parseInt(e.target.value || "1", 10))))}
                  className="hp-input w-20 text-center"
                  data-testid="setup-round-input"
                />
                <span className="text-[11px] text-ink-500">
                  {candidateRoundCount === 0
                    ? "(starts a new candidate journey)"
                    : `(default: Round ${candidateRoundCount + 1})`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <label className="w-[72px] text-[11px] uppercase tracking-wider text-ink-500">Role</label>
                <select
                  value={roleId}
                  onChange={(e) => setRoleId(e.target.value)}
                  className="hp-input flex-1"
                  data-testid="setup-role-select"
                >
                  <option value={DEFAULT_ROLE_SENTINEL}>{DEFAULT_ROLE_TITLE} (default)</option>
                  {roles.map((r) => (
                    <option key={r.role_id} value={r.role_id}>{r.title}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="w-[72px] text-[11px] uppercase tracking-wider text-ink-500">Type</label>
                <select
                  value={interviewType}
                  onChange={(e) => setInterviewType(e.target.value)}
                  className="hp-input flex-1 capitalize"
                  data-testid="setup-type-select"
                >
                  {INTERVIEW_TYPES.map((t) => (
                    <option key={t} value={t} className="capitalize">{t}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="w-[72px] text-[11px] uppercase tracking-wider text-ink-500">Title</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={candidateName ? `${candidateName} — Round ${roundNumber || 1}` : "Untitled interview"}
                  className="hp-input flex-1"
                  data-testid="setup-title-input"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-1 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="hp-btn-ghost"
          data-testid="setup-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onContinue}
          disabled={!ready}
          className="hp-btn-primary"
          data-testid="setup-continue"
        >
          Continue
        </button>
      </div>
    </>
  );
}
